import { FilterOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import type { Dayjs } from "dayjs";
import {
  Form,
  Radio,
  Button,
  DatePicker,
  Select,
  type FormProps,
  Flex,
  theme,
} from "antd";
import { useEffect, useState } from "react";
import { CrudFilters, useTranslate } from "@refinedev/core";
import { useLocalStorage } from "@/hooks/useLocalStorage";

const { RangePicker } = DatePicker;

export const SellersFilters: React.FC<{
  formProps: FormProps;
  filters?: CrudFilters;
  sellersSelectProps?: any;
  storesSelectProps?: any;
  t?: any;
}> = ({ formProps, filters, sellersSelectProps, storesSelectProps, t }) => {
  const [calendarValue, setCalendarValue] = useState<any>();
  const [showRangePicker, setShowRangePicker] = useState(false);
  const [radioValue, setRadioValue] = useLocalStorage<any>("RadioCalendar", []);

  const { token } = theme.useToken();

  const handleRangeChange = (dates: null | (Dayjs | null)[]) => {
    if (dates) {
      formProps.form?.setFieldsValue({ calendar: dates });
    } else {
      formProps.form?.setFieldsValue({ calendar: undefined });
    }
  };

  useEffect(() => {
    formProps.form?.setFieldsValue({ calendar: calendarValue });
  }, [calendarValue]);

  const thisWeek: [Dayjs, Dayjs] = [
    dayjs().startOf("week"),
    dayjs().endOf("week"),
  ];
  const thisMonth: [Dayjs, Dayjs] = [
    dayjs().startOf("month"),
    dayjs().endOf("month"),
  ];

  const rangePresets = [
    { label: t("filter.calendar.all"), value: "" },
    { label: t("filter.calendar.thisWeek"), value: "thisweek" },
    { label: t("filter.calendar.thisMonth"), value: "thismonth" },
    { label: t("filter.calendar.personalized"), value: "personalized" },
  ];

  useEffect(() => {
    radioValue === "personalized"
      ? setShowRangePicker(true)
      : setShowRangePicker(false);
    /* @ts-expect-error Ant Design Icon's v5.0.1 has an issue with @types/react@^18.2.66 */
    const calendarFilter = filters?.find((f) => f.field === "calendar");
    if (calendarFilter) {
      const [start, end] = calendarFilter.value || [];
      if (start && end) {
        setCalendarValue([dayjs(start), dayjs(end)]);
        if (start.isSame(thisWeek[0]) && end.isSame(thisWeek[1])) {
          setRadioValue("thisweek");
          setShowRangePicker(false);
        } else if (start.isSame(thisMonth[0]) && end.isSame(thisMonth[1])) {
          setRadioValue("thismonth");
          setShowRangePicker(false);
        } else {
          setRadioValue("personalized");
        }
      }
    }
  }, [filters]);

  useEffect(() => {
    // Sincroniza el formulario con los filtros
    if (filters) {
      /* @ts-expect-error Ant Design Icon's v5.0.1 has an issue with @types/react@^18.2.66 */
      const calendarFilter = filters.find((f) => f.field === "calendar");
      /* @ts-expect-error Ant Design Icon's v5.0.1 has an issue with @types/react@^18.2.66 */
      const sellersFilter = filters.find((f) => f.field === "name");
      /* @ts-expect-error Ant Design Icon's v5.0.1 has an issue with @types/react@^18.2.66 */
      const storesFilter = filters.find((f) => f.field === "linked_store");
      /* @ts-expect-error Ant Design Icon's v5.0.1 has an issue with @types/react@^18.2.66 */
      const stateFilter = filters.find((f) => f.field === "state");

      formProps.form?.setFieldsValue({
        calendar: calendarFilter?.value || undefined,
        sellers: sellersFilter?.value || undefined,
        stores: storesFilter?.value || undefined,
        state: stateFilter?.value || undefined,
      });
    }
  }, [filters]);

  const handleRadioChange = (e: any) => {
    const { value } = e.target;
    setRadioValue(value);

    if (value === "personalized") {
      setShowRangePicker(true);
      setCalendarValue(value);
    } else if (value === "thisweek") {
      setShowRangePicker(false);
      setCalendarValue(thisWeek);
    } else if (value === "thismonth") {
      setShowRangePicker(false);
      setCalendarValue(thisMonth);
    } else {
      setShowRangePicker(false);
      setCalendarValue(undefined);
    }
  };

  function clearFilters() {
    formProps.form?.resetFields();
    setCalendarValue(undefined);
    setRadioValue("");
    setShowRangePicker(false);
    formProps.form?.submit();
  }

  return (
    <Form layout="vertical" {...formProps} style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>
      <div style={{ flex: 1, overflowY: "auto", padding: "16px 24px" }}>
      <Form.Item
        label={
          <span style={{ color: token.colorTextTertiary }}>
            {t("filter.calendar.lastUpdate")}
          </span>
        }
        name="calendar"
      >
        <Flex vertical>
          <Radio.Group
            value={radioValue}
            style={{
              display: "flex",
              gap: "10px",
              marginBottom: showRangePicker ? "10px" : "",
            }}
            size="small"
            onChange={handleRadioChange}
          >
            {rangePresets.map((preset, i) => (
              <Radio.Button key={i} value={preset.value} name={preset.value}>
                {preset.label}
              </Radio.Button>
            ))}
          </Radio.Group>
          {showRangePicker && (
            <RangePicker onChange={handleRangeChange} required />
          )}
        </Flex>
      </Form.Item>

      <Form.Item
        label={
          <span style={{ color: token.colorTextTertiary }}>
            {t("create.seller.labelSellers")}
          </span>
        }
        name="sellers"
      >
        <Flex vertical>
          <Select
            allowClear
            style={{ width: "100%" }}
            placeholder={t("filter.select.placeholder")}
            {...sellersSelectProps}
            /* @ts-expect-error Ant Design Icon's v5.0.1 has an issue with @types/react@^18.2.66 */
            defaultValue={filters?.find((f) => f.field === "name")?.value}
            onChange={(value) => {
              formProps.form?.setFieldsValue({ sellers: value });
            }}
          />
        </Flex>
      </Form.Item>

      <Form.Item
        label={
          <span style={{ color: token.colorTextTertiary }}>
            {t("filter.select.labelStores")}
          </span>
        }
        name="stores"
      >
        <Flex vertical>
          <Select
            allowClear
            style={{ width: "100%" }}
            placeholder={t("filter.select.placeholder")}
            {...storesSelectProps}
            defaultValue={
              /* @ts-expect-error Ant Design Icon's v5.0.1 has an issue with @types/react@^18.2.66 */
              filters?.find((f) => f.field === "linked_store")?.value
            }
            onChange={(value) => {
              formProps.form?.setFieldsValue({ stores: value });
            }}
          />
        </Flex>
      </Form.Item>

      <Form.Item
        label={
          <span style={{ color: token.colorTextTertiary }}>
            {t("filter.select.labelStatus")}
          </span>
        }
        name="state"
      >
        <Flex vertical>
          <Select
            allowClear
            placeholder={t("filter.select.placeholder")}
            options={[
              {
                label: t("filter.products.labelStatusActive"),
                value: "active",
              },
              {
                label: "Paused",
                value: "paused",
              },
            ]}
            /* @ts-expect-error Ant Design Icon's v5.0.1 has an issue with @types/react@^18.2.66 */
            defaultValue={filters?.find((f) => f.field === "state")?.value}
            onChange={(value) => {
              formProps.form?.setFieldsValue({ state: value });
            }}
          />
        </Flex>
      </Form.Item>
      </div>
      <Flex
        align="center"
        justify="end"
        gap="8px"
        style={{ flexShrink: 0, padding: "12px 24px", borderTop: "1px solid #f0f0f0", backgroundColor: "#fff" }}
      >
        <Button type="default" danger onClick={clearFilters}>
          {t("filter.button.clearFilters")}
        </Button>

        <Button htmlType="submit" type="primary" icon={<FilterOutlined />}>
          {t("filter.button.filter")}
        </Button>
      </Flex>
    </Form>
  );
};
